import { formatDateTime, formatMoney } from '@app/components'
import { Prose, usePublic } from '@app/components/public'
import { http } from '@app/lib/api'
import { isoToZoned } from '@lib/time'
import {
  nextScholarship,
  nextScholarshipBoundary,
  openScholarships,
  type PublicScholarship,
  type PublicScholarshipsPayload,
  scholarshipsHref,
} from '@shared/contracts/scholarships'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useId, useState } from 'react'
import { Link } from 'react-router'

const SCHOLARSHIPS_PATH = '/api/v1/public/scholarships'
const EMPTY: PublicScholarship[] = []
const HOUR = 3_600_000

/**
 * Re-renders just after the next time a program opens or closes (and at least hourly), so the
 * hero, the home row and every scholarships link switch on their own at the deadline, even when
 * the page has been open for hours or the payload came from the 60 s cache.
 */
function useScholarshipClock(items: PublicScholarship[]) {
  const [tick, setTick] = useState(0)
  const now = new Date().toISOString()
  // biome-ignore lint/correctness/useExhaustiveDependencies: `tick` re-arms the timer after it fires
  useEffect(() => {
    const at = new Date().toISOString()
    const boundary = nextScholarshipBoundary(items, at)
    const wait = boundary ? Math.min(Date.parse(boundary) - Date.parse(at), HOUR) : HOUR
    const t = window.setTimeout(() => setTick((n) => n + 1), Math.max(0, wait) + 250)
    return () => window.clearTimeout(t)
  }, [items, tick])
  return now
}

/** The public scholarships payload, evaluated against the browser's clock. */
export function useScholarships() {
  const q = usePublic<PublicScholarshipsPayload>('scholarships', SCHOLARSHIPS_PATH)
  const items = q.data?.items ?? EMPTY
  const now = useScholarshipClock(items)
  return {
    isLoading: q.isLoading,
    error: q.error,
    items,
    now,
    open: openScholarships(items, now),
    next: nextScholarship(items, now),
    href: scholarshipsHref(items, now),
    fund: q.data?.fund ?? null,
  }
}

/** Where every "Scholarships" link goes: the single open program's page, else the list. */
export function useScholarshipsLink(): string {
  return useScholarships().href
}

/** The scholarship program an application form belongs to (only looked up for scholarship forms). */
export function useScholarshipForForm(
  formSlug: string | undefined,
  formKind: string | undefined,
): PublicScholarship | null {
  const enabled = formKind === 'scholarship' && !!formSlug
  const q = useQuery({
    queryKey: ['public', 'scholarships'],
    queryFn: () => http.get<PublicScholarshipsPayload>(SCHOLARSHIPS_PATH),
    staleTime: 5 * 60_000,
    enabled,
  })
  if (!enabled) return null
  return q.data?.items.find((s) => s.formSlug === formSlug) ?? null
}

/** "Give to the scholarship fund" link when no single program is in focus. */
export function scholarshipFundHref(fund: { code: string } | null) {
  return fund ? `/donate?fund=${encodeURIComponent(fund.code)}` : '/donate'
}

/** The hero title: the program's name exactly as staff wrote it (no year is added). */
export function scholarshipTitle(s: Pick<PublicScholarship, 'name'>) {
  return s.name.trim()
}

/** Whole Central-time calendar days from `now` to `iso` (0 = same day in Burlington). */
export function centralDaysUntil(iso: string, now: string) {
  const day = (v: string) => Date.parse(`${isoToZoned(v).date}T00:00:00Z`)
  return Math.round((day(iso) - day(now)) / 86_400_000)
}

const deadlineFmt: Intl.DateTimeFormatOptions = {
  month: 'long',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZoneName: 'short',
}

/** "Closes today at 11:59 PM CDT" / "3 days left · closes March 31 at 10:59 PM CDT". */
export function deadlineText(closesAt: string, now: string) {
  const days = centralDaysUntil(closesAt, now)
  const when = formatDateTime(closesAt, deadlineFmt)
  if (days <= 0)
    return `Closes today at ${formatDateTime(closesAt, { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })}`
  if (days === 1) return `1 day left · closes ${when}`
  return `${days} days left · closes ${when}`
}

const shortDate = (iso: string) => formatDateTime(iso, { month: 'long', day: 'numeric' })

function awardLine(s: PublicScholarship) {
  if (!s.awardAmountCents) return null
  return s.awardCount === 1
    ? `One award of ${formatMoney(s.awardAmountCents)}`
    : `${s.awardCount} awards of ${formatMoney(s.awardAmountCents)}`
}

const applyHref = (s: PublicScholarship) =>
  s.formSlug ? `/apply/${s.formSlug}` : `/scholarships/${s.slug}`

/** Eligibility requirements, shown first on a scholarship's page and its application. */
export function EligibilityBox({ html, intro }: { html: string; intro?: string }) {
  const id = useId()
  if (!html.trim()) return null
  return (
    <section className="card eligibility-box stack--sm" aria-labelledby={id}>
      <h2 id={id} className="eligibility-box__title">
        Eligibility
      </h2>
      {intro ? <p className="small mb-0">{intro}</p> : null}
      <Prose html={html} />
    </section>
  )
}

/** Home hero while scholarship applications are open (the festival-day hero takes priority). */
export function ScholarshipHero({
  open,
  fund,
}: {
  open: PublicScholarship[]
  fund: { code: string } | null
}) {
  const first = open[0]
  if (!first) return null
  if (open.length > 1) {
    return (
      <section className="hero hero--scholarship" aria-labelledby="hero-h">
        <span className="hero__eyebrow">Scholarship applications open</span>
        <h1 id="hero-h">Scholarships are open</h1>
        <p>
          {open.length} scholarships are accepting applications.
          {first.closesAt ? ` The first deadline is ${shortDate(first.closesAt)}.` : ''}
        </p>
        <div className="cluster">
          <Link to="/scholarships" className="btn btn--lg">
            See scholarships and apply
          </Link>
          <Link to={scholarshipFundHref(fund)} className="btn btn--lg hero__donate">
            Donate to the scholarship fund
          </Link>
        </div>
      </section>
    )
  }
  const s = first
  const award = awardLine(s)
  return (
    <section className="hero hero--scholarship" aria-labelledby="hero-h">
      <span className="hero__eyebrow">Scholarship applications open</span>
      <h1 id="hero-h">
        {scholarshipTitle(s)} is open
        {s.closesAt ? ` — apply by ${shortDate(s.closesAt)}` : ''}
      </h1>
      {s.summary ? <p>{s.summary}</p> : null}
      <p className="hero__meta">
        {award ? <strong>{award}</strong> : null}
        {award && s.closesAt ? ' · ' : null}
        {s.closesAt ? `Deadline ${formatDateTime(s.closesAt, deadlineFmt)}` : null}
      </p>
      <div className="cluster">
        <Link to={applyHref(s)} className="btn btn--lg">
          Apply now
        </Link>
        <Link to={s.donateHref} className="btn btn--lg hero__donate">
          Donate to the scholarship
        </Link>
      </div>
      <Link to={`/scholarships/${s.slug}`} className="hero__link">
        Eligibility and details
      </Link>
    </section>
  )
}

/** The scholarships half of the home page's programs row. */
export function ScholarshipHomePanel({
  open,
  next,
  fund,
  now,
  href,
}: {
  open: PublicScholarship[]
  next: PublicScholarship | null
  fund: { code: string } | null
  now: string
  href: string
}) {
  return (
    <section className="stack--sm" aria-labelledby="home-scholarships-h">
      <div className="section__head">
        <h2 id="home-scholarships-h">Scholarships</h2>
        <Link to={href} className="small">
          {open.length === 1 ? 'Details →' : 'All scholarships →'}
        </Link>
      </div>
      {open.length > 0 ? (
        open.map((s) => (
          <article key={s.id} className="card stack--sm" aria-labelledby={`sch-${s.id}`}>
            <span className="badge badge--success">Accepting applications</span>
            <h3 id={`sch-${s.id}`} className="card__title">
              {s.name}
            </h3>
            {awardLine(s) ? <p className="small mb-0">{awardLine(s)}</p> : null}
            {s.closesAt ? <p className="small bold mb-0">{deadlineText(s.closesAt, now)}</p> : null}
            <div className="cluster">
              <Link
                to={applyHref(s)}
                className="btn btn--sm"
                aria-label={`Apply for the ${s.name}`}
              >
                Apply
              </Link>
              <Link
                to={s.donateHref}
                className="btn btn--sm btn--secondary"
                aria-label={`Donate to the ${s.name} fund`}
              >
                Donate to the fund
              </Link>
            </div>
          </article>
        ))
      ) : next?.opensAt ? (
        <article className="card stack--sm" aria-labelledby="sch-next">
          <span className="badge badge--info">Coming soon</span>
          <h3 id="sch-next" className="card__title">
            {next.name}
          </h3>
          <p className="small bold mb-0">
            Applications open {formatDateTime(next.opensAt, { dateStyle: 'long' })}
          </p>
          {awardLine(next) ? <p className="small mb-0">{awardLine(next)}</p> : null}
          <div className="cluster">
            <Link to={`/scholarships/${next.slug}`} className="btn btn--sm btn--secondary">
              Eligibility and details
            </Link>
            <Link to={next.donateHref} className="btn btn--sm btn--secondary">
              Donate to the fund
            </Link>
          </div>
        </article>
      ) : (
        <article className="card card--muted stack--sm" aria-labelledby="sch-fund">
          <h3 id="sch-fund" className="card__title">
            Fund a scholarship
          </h3>
          <p className="small mb-0">
            Every dollar given to the Scholarship Fund goes directly to LGBTQ+ students and allies
            from Des Moines County.
          </p>
          <div className="cluster">
            <Link to={scholarshipFundHref(fund)} className="btn btn--sm">
              Give to the Scholarship Fund
            </Link>
          </div>
        </article>
      )}
    </section>
  )
}
